{
  "foundations": [
    {
      "slug": "design-token",
      "title": "Design Token",
      "description": "A semantic vocabulary that keeps decisions portable across themes and products.",
      "intent": "Describe purpose before appearance so components can adapt without changing meaning.",
      "rules": [
        "Name roles by intent",
        "Map aliases through one source",
        "Keep light and dark roles parallel"
      ],
      "values": [
        [
          "primary",
          "Action and focus",
          "#0066CC"
        ],
        [
          "canvas",
          "Page ground",
          "#F7F8FA"
        ],
        [
          "surface",
          "Content surface",
          "#FFFFFF"
        ]
      ],
      "accessibility": [
        "Token changes preserve contrast",
        "Status roles are not color only"
      ],
      "related": [
        "color",
        "spacing",
        "state"
      ],
      "specimen": "design-token"
    },
    {
      "slug": "color",
      "title": "Color",
      "description": "Cool neutrals create quiet structure while one blue signals interaction.",
      "intent": "Clarify hierarchy, action, and state without turning color into decoration.",
      "rules": [
        "Reserve blue for interaction",
        "Preserve semantic status colors",
        "Verify both themes"
      ],
      "values": [
        [
          "primary",
          "Light action",
          "#0066CC"
        ],
        [
          "dark-primary",
          "Dark action",
          "#78B7FF"
        ],
        [
          "ink",
          "Primary content",
          "#17181A"
        ]
      ],
      "accessibility": [
        "Body text reaches 4.5 to 1",
        "Non-text controls reach 3 to 1"
      ],
      "related": [
        "design-token",
        "state",
        "inclusive-design"
      ],
      "specimen": "color"
    },
    {
      "slug": "typography",
      "title": "Typography",
      "description": "A compact Geist scale for product content, supporting text, and code.",
      "intent": "Carry hierarchy through size, weight, measure, and spacing before adding surfaces.",
      "rules": [
        "Use sentence case",
        "Balance headings and pretty-wrap prose",
        "Reserve mono for functional data"
      ],
      "values": [
        [
          "display",
          "Hero statement",
          "64 / 1.02"
        ],
        [
          "body-md",
          "Product copy",
          "16 / 1.55"
        ],
        [
          "caption",
          "Metadata",
          "12 / 1.4"
        ]
      ],
      "accessibility": [
        "Text reflows at 200 percent",
        "CJK keeps semantic word groups"
      ],
      "related": [
        "writing",
        "international-design",
        "layout"
      ],
      "specimen": "typography"
    },
    {
      "slug": "iconography",
      "title": "Iconography",
      "description": "Consistent geometric icons support scanning without replacing labels.",
      "intent": "Use one visual grammar and let text carry meaning when an icon is unfamiliar.",
      "rules": [
        "Keep stroke weight consistent",
        "Use optical alignment",
        "Avoid metaphor clichés"
      ],
      "values": [
        [
          "small",
          "Inline support",
          "16 px"
        ],
        [
          "control",
          "Standard control",
          "20 px"
        ],
        [
          "feature",
          "Illustrative use",
          "24 px"
        ]
      ],
      "accessibility": [
        "Decorative icons are hidden",
        "Icon-only controls have accessible names"
      ],
      "related": [
        "typography",
        "inclusive-design",
        "components"
      ],
      "specimen": "iconography"
    },
    {
      "slug": "elevation",
      "title": "Elevation",
      "description": "Surface contrast and restrained shadow explain real stacking relationships.",
      "intent": "Show containment and layering without making every region float.",
      "rules": [
        "Use tone before shadow",
        "Match shadow to stacking",
        "Keep one lighting direction"
      ],
      "values": [
        [
          "flat",
          "Page content",
          "0"
        ],
        [
          "contained",
          "Cards and bars",
          "1"
        ],
        [
          "floating",
          "Menus and popovers",
          "2"
        ],
        [
          "modal",
          "Dialogs",
          "3"
        ]
      ],
      "accessibility": [
        "Selection does not rely on elevation",
        "High contrast preserves boundaries"
      ],
      "related": [
        "color",
        "radius",
        "state"
      ],
      "specimen": "elevation"
    },
    {
      "slug": "gradient",
      "title": "Gradient",
      "description": "Controlled ramps serve data, masks, and the established hero text treatment.",
      "intent": "Use continuous color only when continuity itself communicates information.",
      "rules": [
        "Keep interface backgrounds flat",
        "Label data ramps",
        "Avoid decorative AI gradients"
      ],
      "values": [
        [
          "data-ramp",
          "Ordered magnitude",
          "5 stops"
        ],
        [
          "mask",
          "Edge continuity",
          "alpha"
        ],
        [
          "hero-text",
          "Heading emphasis",
          "one exception"
        ]
      ],
      "accessibility": [
        "Ramp endpoints meet contrast",
        "Data has a non-color equivalent"
      ],
      "related": [
        "color",
        "chart",
        "inclusive-design"
      ],
      "specimen": "gradient"
    },
    {
      "slug": "inclusive-design",
      "title": "Inclusive Design",
      "description": "Semantics, contrast, focus, and forgiving interaction shape every component.",
      "intent": "Make the primary task operable across perception, input, zoom, and motion preferences.",
      "rules": [
        "Prefer native semantics",
        "Keep focus visible",
        "Design recovery beside failure"
      ],
      "values": [
        [
          "target",
          "Touch target",
          "44 × 44 px"
        ],
        [
          "focus",
          "Visible ring",
          "2 px + offset"
        ],
        [
          "reflow",
          "Minimum width",
          "320 CSS px"
        ]
      ],
      "accessibility": [
        "WCAG 2.2 AA is the baseline",
        "Assistive technology follows the same task order"
      ],
      "related": [
        "state",
        "motion",
        "international-design"
      ],
      "specimen": "inclusive-design"
    },
    {
      "slug": "international-design",
      "title": "International Design",
      "description": "Layouts and content survive translation, direction, and locale-specific formats.",
      "intent": "Treat language expansion and direction as normal product states rather than exceptions.",
      "rules": [
        "Allow 30 percent expansion",
        "Use logical properties",
        "Format dates and numbers by locale"
      ],
      "values": [
        [
          "expansion",
          "Copy allowance",
          "30%"
        ],
        [
          "direction",
          "Layout logic",
          "LTR and RTL"
        ],
        [
          "locale",
          "Formatting",
          "Intl APIs"
        ]
      ],
      "accessibility": [
        "Language and direction attributes are correct",
        "Translated labels remain complete"
      ],
      "related": [
        "typography",
        "layout",
        "writing"
      ],
      "specimen": "international-design"
    },
    {
      "slug": "layout",
      "title": "Layout",
      "description": "Responsive containers and durable reading order preserve task priority.",
      "intent": "Scale density and columns without changing meaning or moving essential actions unpredictably.",
      "rules": [
        "Start from source order",
        "Use grid for multi-axis layout",
        "Cap readable measures"
      ],
      "values": [
        [
          "gutter",
          "Compact page",
          "16 px"
        ],
        [
          "content",
          "Reading measure",
          "65 characters"
        ],
        [
          "workspace",
          "Maximum shell",
          "1440 px"
        ]
      ],
      "accessibility": [
        "No page overflow at 320 CSS pixels",
        "Zoom preserves task order"
      ],
      "related": [
        "spacing",
        "radius",
        "international-design"
      ],
      "specimen": "layout"
    },
    {
      "slug": "motion",
      "title": "Motion",
      "description": "Cause, direction, and continuity make state changes easier to understand.",
      "intent": "Explain what changed without delaying input or adding decorative travel.",
      "rules": [
        "Start feedback within 100 milliseconds",
        "Animate transform and opacity",
        "Provide reduced-motion parity"
      ],
      "values": [
        [
          "instant",
          "Press feedback",
          "100 ms"
        ],
        [
          "standard",
          "State change",
          "180–240 ms"
        ],
        [
          "spatial",
          "Panel travel",
          "240–360 ms"
        ]
      ],
      "accessibility": [
        "Reduced motion removes travel",
        "State remains visible without animation"
      ],
      "related": [
        "state",
        "elevation",
        "inclusive-design"
      ],
      "specimen": "motion"
    },
    {
      "slug": "radius",
      "title": "Radius",
      "description": "A restrained shape scale keeps containers soft but precise.",
      "intent": "Express containment and nesting without turning every control into a pill.",
      "rules": [
        "Use smaller radii inside larger surfaces",
        "Reserve pills for status",
        "Keep seams square"
      ],
      "values": [
        [
          "sm",
          "Tags and fragments",
          "6 px"
        ],
        [
          "md",
          "Controls",
          "10 px"
        ],
        [
          "lg",
          "Cards",
          "14 px"
        ],
        [
          "xl",
          "Dialogs",
          "20 px"
        ]
      ],
      "accessibility": [
        "Shape is not the only state cue",
        "Focus rings follow the visible contour"
      ],
      "related": [
        "elevation",
        "spacing",
        "components"
      ],
      "specimen": "radius"
    },
    {
      "slug": "spacing",
      "title": "Spacing",
      "description": "A four-pixel scale separates decisions and groups related content.",
      "intent": "Build hierarchy through rhythm before adding borders, cards, or decorative fill.",
      "rules": [
        "Use documented steps",
        "Prefer gap over margins between siblings",
        "Adjust section rhythm optically"
      ],
      "values": [
        [
          "xs",
          "Tight labels",
          "4 px"
        ],
        [
          "sm",
          "Control internals",
          "8 px"
        ],
        [
          "md",
          "Related content",
          "16 px"
        ],
        [
          "section",
          "Major separation",
          "80 px"
        ]
      ],
      "accessibility": [
        "Touch targets keep adequate separation",
        "Text expansion does not collapse gaps"
      ],
      "related": [
        "layout",
        "radius",
        "typography"
      ],
      "specimen": "spacing"
    },
    {
      "slug": "state",
      "title": "State",
      "description": "A shared lifecycle distinguishes pending, usable, empty, and recoverable outcomes.",
      "intent": "Show what is happening, what remains safe, and what action can recover the task.",
      "rules": [
        "Keep previous data while refreshing",
        "Use skeletons only for known geometry",
        "Place errors near recovery"
      ],
      "values": [
        [
          "pending",
          "No usable data",
          "skeleton"
        ],
        [
          "refreshing",
          "Previous data remains",
          "inline status"
        ],
        [
          "error",
          "Recovery available",
          "retry"
        ]
      ],
      "accessibility": [
        "Status changes are announced appropriately",
        "Focus does not jump without intent"
      ],
      "related": [
        "motion",
        "inclusive-design",
        "writing"
      ],
      "specimen": "state"
    },
    {
      "slug": "voice-and-tone",
      "title": "Voice and Tone",
      "description": "Direct, calm language changes emphasis according to risk and context.",
      "intent": "Help people decide and recover without vague enthusiasm or blame.",
      "rules": [
        "Lead with the outcome",
        "Name objects and consequences",
        "Match urgency to actual risk"
      ],
      "values": [
        [
          "default",
          "Routine guidance",
          "calm"
        ],
        [
          "success",
          "Confirmed outcome",
          "concise"
        ],
        [
          "error",
          "Recovery",
          "specific"
        ]
      ],
      "accessibility": [
        "Messages avoid idioms",
        "Urgent copy remains understandable without punctuation tricks"
      ],
      "related": [
        "writing",
        "state",
        "international-design"
      ],
      "specimen": "voice-and-tone"
    },
    {
      "slug": "writing",
      "title": "Writing",
      "description": "Specific interface copy names actions, outcomes, states, and recovery paths.",
      "intent": "Reduce interpretation by writing the next decision in plain, active language.",
      "rules": [
        "Use active voice",
        "Label actions with verb and result",
        "Keep error recovery inline"
      ],
      "values": [
        [
          "button",
          "Action label",
          "verb + result"
        ],
        [
          "error",
          "Recovery copy",
          "cause + next step"
        ],
        [
          "empty",
          "Next action",
          "context + path"
        ]
      ],
      "accessibility": [
        "Labels make sense out of visual context",
        "Translation does not concatenate fragments"
      ],
      "related": [
        "voice-and-tone",
        "state",
        "typography"
      ],
      "specimen": "writing"
    }
  ]
}
